import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon, CheckCircle2Icon, MailIcon } from 'lucide-react';
import { PAIN_INTRO, PAINS, STORY_BLOCKS, PROBLEM_SECTION, SOLUTION_INTRO } from '../constants/content';
import { CtaButton } from './CtaButton';
function Reveal({
  children
}: {
  children: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.5
  }} data-id="element-195" data-mp="3luxfc9gnzq5w">
      {children}
    </motion.div>;
}
export function SalesLetter() {
  return <section className="w-full bg-cream py-14 sm:py-20" data-id="element-196" data-mp="8ncdpzly5163">
      <div className="mx-auto max-w-[800px] px-4" data-id="element-197" data-mp="8kq7c3askrpg">
        {/* Letter header */}
        <Reveal data-id="element-198" data-mp="1639i0cxqnvmk">
          <div className="mb-8 border-l-4 border-crimson bg-white p-5 shadow-sm" data-id="element-199" data-mp="163roe2e47q87">
            <p className="flex items-center gap-2 text-sm font-600 uppercase tracking-wide text-crimson" data-id="element-200" data-mp="119x2csb1ez5y">
              <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-201" data-mp="2n3rlli6d0kaz" />
              {PAIN_INTRO.from}
            </p>
            <p className="mt-1 text-sm italic text-gray-600" data-id="element-202" data-mp="2e9cy4sboe3gs">{PAIN_INTRO.re}</p>
          </div>
        </Reveal>

        <Reveal data-id="element-203" data-mp="2iaq4r4mleaor">
          <p className="mb-2 text-lg font-700" data-id="element-204" data-mp="2ia6mtnwxzmaq">{PAIN_INTRO.greeting}</p>
          <p className="mb-6 font-display text-2xl font-600 text-ink" data-id="element-205" data-mp="24b6zcjl61dg5">
            {PAIN_INTRO.question}
          </p>
        </Reveal>

        {/* Pain points */}
        <ul className="mb-8 space-y-3" data-id="element-206" data-mp="29bl7e1mrone6">
          {PAINS.map((pain, i) => <motion.li key={i} initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.05
        }} className="flex items-start gap-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-100" data-id="element-207" data-mp="1vbtygfb3xx71" data-mpi={i}>
              <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-crimson" aria-hidden="true" data-id="element-208" data-mp="11h9uz3n6szle" />
              <span className="text-base leading-relaxed text-gray-800" data-id="element-209" data-mp="si6co1vugo7y">
                {pain}
              </span>
            </motion.li>)}
        </ul>

        {STORY_BLOCKS.map((block, i) => <Reveal key={i} data-id="element-210" data-mp="1l627f11us623" data-mpi={i}>
            <p className="mb-4 text-lg leading-relaxed text-gray-800" data-id="element-211" data-mp="1l7vfmks46yc7">
              {block}
            </p>
          </Reveal>)}

        <Reveal data-id="element-212" data-mp="1gdirvyjlv0un">
          <p className="my-8 text-center font-display text-2xl font-600 leading-snug text-crimson sm:text-3xl" data-id="element-213" data-mp="1bh0n4yo9z2ib">
            «¡Tener una pollería rentable no debería ser tan difícil!»
          </p>
        </Reveal>

        <Reveal data-id="element-214" data-mp="2456alt6pr5lu">
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-215" data-mp="244o5s85zjj2b">
            <p data-id="element-216" data-mp="2bh4a6ptcbrze">
              Ahora bien, si querés montar tu propio local — o hacer por fin
              rentable el que ya tenés — de forma rápida y sin vueltas…
            </p>
            <p data-id="element-217" data-mp="2lf0g937f7wqn">
              En lugar de perder meses o años a los ponchazos, aprendiendo a las
              patadas con la plata que tanto te costó juntar…
            </p>
            <p data-id="element-218" data-mp="2pe9a082gnum7">
              Y si soñás con la satisfacción de manejar tu propio negocio, con
              tus propias reglas, sin depender de nadie…
            </p>
            <p className="font-600" data-id="element-219" data-mp="2zd8jfsv1vua9">
              Entonces esta puede ser la carta más importante que leas este año…
            </p>
          </div>
        </Reveal>

        {/* Author card */}
        <Reveal data-id="element-220" data-mp="3931jbwltykyo">
          <div className="my-10 flex flex-col items-center gap-4 rounded-2xl bg-white p-6 text-center shadow-md ring-1 ring-black/5 sm:flex-row sm:text-left" data-id="element-221" data-mp="393tcl0ryg6ls">
            <img src="https://cdn.magicpatterns.com/uploads/iJxzY6totSivaTCZ2fZrXX/logo-96.webp" srcSet="https://cdn.magicpatterns.com/uploads/iJxzY6totSivaTCZ2fZrXX/logo-96.webp 1x, https://cdn.magicpatterns.com/uploads/sUkLbPi7ik4paAEb3Ugtb8/logo-192.webp 2x" alt="Isotipo de Pollería Rentable 2026" width={96} height={96} loading="lazy" decoding="async" className="h-24 w-24 shrink-0 rounded-full object-cover ring-4 ring-gold/40" data-id="element-222" data-mp="3i49h8dkfpjs8" />
            <div data-id="element-223" data-mp="2ll9jii0xk1rx">
              <p className="text-base leading-relaxed text-gray-600" data-id="element-224" data-mp="2hi4h0fguo03a">
                Comerciante de toda la vida. Pasé de 2 cajones de pollo a tener
                mi propio local. Creador de Pollería Rentable 2026.
              </p>
            </div>
          </div>
        </Reveal>

        {/* The big problem */}
        <Reveal data-id="element-225" data-mp="aqvi8jzwfh5e">
          <h2 className="mb-4 font-display text-3xl font-700 text-ink sm:text-4xl" data-id="element-226" data-mp="aqbzj12fcoqb">
            «{PROBLEM_SECTION.title}»
          </h2>
          <p className="mb-2 text-lg leading-relaxed text-gray-800" data-id="element-227" data-mp="5uel50aq1sg6">
            {PROBLEM_SECTION.intro}
          </p>
          <p className="mb-4 text-lg font-700 text-crimson" data-id="element-228" data-mp="1sp0btxk4b5hz">
            {PROBLEM_SECTION.subtitle}
          </p>
        </Reveal>

        <ul className="mb-8 space-y-3" data-id="element-229" data-mp="1eq0jaethi6gj">
          {PROBLEM_SECTION.bullets.map((b, i) => <li key={i} className="flex items-start gap-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-100" data-id="element-230" data-mp="1jnk0nnlhazcp" data-mpi={i}>
              <XCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-crimson" aria-hidden="true" data-id="element-231" data-mp="27g0uswihqhsd" />
              <span className="text-base leading-relaxed text-gray-800" data-id="element-232" data-mp="1xomq8abomrsd">
                {b}
              </span>
            </li>)}
        </ul>

        <Reveal data-id="element-233" data-mp="22l3b8vnl4q18">
          <p className="mb-8 text-lg leading-relaxed text-gray-800" data-id="element-234" data-mp="2qcs9str02aqm">
            Y en la mayoría de los casos, directamente no hay ningún detalle
            real.
          </p>
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-235" data-mp="2v4tladgcfxfl">
            {PROBLEM_SECTION.outro.map((p, i) => <p key={i} data-id="element-236" data-mp="300nsat6raes3" data-mpi={i}>{p}</p>)}
          </div>
        </Reveal>

        <Reveal data-id="element-237" data-mp="2zybgkxrld0nh">
          <p className="my-8 text-center font-display text-2xl font-600 leading-snug text-ink sm:text-3xl" data-id="element-238" data-mp="2v1b9qkmx3tvb">
            ¡Con razón parece tan complicado abrir tu pollería!
          </p>
        </Reveal>

        {/* The solution */}
        <Reveal data-id="element-239" data-mp="2m1ofqjwipqca">
          <h2 className="mb-4 font-display text-3xl font-700 text-ink sm:text-4xl" data-id="element-240" data-mp="2m268v7nvvofz">
            {SOLUTION_INTRO.title}
          </h2>
          <div className="space-y-4 text-lg leading-relaxed text-gray-800" data-id="element-241" data-mp="2h81ysfjbn0ao">
            {SOLUTION_INTRO.paragraphs.map((p, i) => <p key={i} className="flex items-start gap-3" data-id="element-242" data-mp="27ci6s47volr7" data-mpi={i}>
                <CheckCircle2Icon className="mt-1 h-6 w-6 shrink-0 text-green-600" aria-hidden="true" data-id="element-243" data-mp="279vyu3h2s48g" />
                <span data-id="element-244" data-mp="1ycbx6tfaw5xb">{p}</span>
              </p>)}
          </div>
        </Reveal>

        <Reveal data-id="element-245" data-mp="drygzwu8jmhe">
          <div className="my-8 flex justify-center" data-id="element-246" data-mp="dsgip8zo2swp">
            <div className="w-full max-w-md" data-id="element-247" data-mp="hu24jju6ihj1">
              <CtaButton data-id="element-248" data-mp="ht0jcmjg8enl">COMPRAR AHORA</CtaButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}